import { createRouter, createWebHistory } from 'vue-router'
import { useAuth } from '@dpweb/shared'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('../views/HomeView.vue'),
    },
    {
      path: '/product/:slug?',
      name: 'product',
      component: () => import('../views/ProductView.vue'),
    },
    {
      path: '/cart',
      name: 'cart',
      component: () => import('../views/CartView.vue'),
      meta: { requiresAuth: true, userType: 'userx' },
    },
    {
      path: '/checkout',
      name: 'checkout',
      component: () => import('../views/CheckoutView.vue'),
      meta: { requiresAuth: true, userType: 'userx' },
    },
    {
      path: '/orders',
      name: 'orders',
      component: () => import('../views/OrdersView.vue'),
      meta: { requiresAuth: true, userType: 'userx' },
    },
    {
      path: '/account',
      name: 'account',
      component: () => import('../views/AccountView.vue'),
      meta: { requiresAuth: true, userType: 'userx' },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue'),
      meta: { guest: true },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('../views/RegisterView.vue'),
      meta: { guest: true },
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('../views/VerifyEmailView.vue'),
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('../views/PasswordResetRequestView.vue'),
      meta: { guest: true },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('../views/PasswordResetConfirmView.vue'),
    },
    {
      path: '/catalog',
      name: 'catalog',
      component: () => import('../views/CatalogView.vue'),
    },
    {
      path: '/catalog/:id',
      name: 'catalog-detail',
      component: () => import('../views/CatalogDetailView.vue'),
    },

    // Admin routes share this SPA and use their own URL namespace and shell.
    {
      path: '/office',
      name: 'admin-dashboard',
      component: () => import('../views/admin/DashboardView.vue'),
      meta: { admin: true, requiresAdmin: true },
    },
    {
      path: '/office/change-password',
      name: 'admin-change-password',
      component: () => import('../views/admin/ChangePasswordView.vue'),
      meta: { admin: true, requiresAdmin: true },
    },
    {
      path: '/office/login',
      name: 'admin-login',
      component: () => import('../views/admin/LoginView.vue'),
      meta: { admin: true, adminGuest: true },
    },
    {
      path: '/office/forgot-password',
      name: 'admin-forgot-password',
      component: () => import('../views/admin/PasswordResetRequestView.vue'),
      meta: { admin: true, adminGuest: true },
    },
    {
      path: '/office/reset-password',
      name: 'admin-reset-password',
      component: () => import('../views/admin/PasswordResetConfirmView.vue'),
      meta: { admin: true },
    },
    {
      path: '/office/items',
      name: 'admin-items',
      component: () => import('../views/admin/ItemsView.vue'),
      meta: { admin: true, requiresAdmin: true, requiresRole: 1 },
    },
    {
      path: '/office/stocks',
      name: 'admin-stocks',
      component: () => import('../views/admin/StocksView.vue'),
      meta: { admin: true, requiresAdmin: true, requiresRole: 1 },
    },
    {
      path: '/office/customers',
      name: 'admin-customers',
      component: () => import('../views/admin/CustomersView.vue'),
      meta: { admin: true, requiresAdmin: true, requiresRole: 1 },
    },
    {
      path: '/office/orders',
      name: 'admin-orders',
      component: () => import('../views/admin/OrdersView.vue'),
      meta: { admin: true, requiresAdmin: true, requiresRole: 1 },
    },
    {
      path: '/office/mails',
      name: 'admin-mails',
      component: () => import('../views/admin/MailsView.vue'),
      meta: { admin: true, requiresAdmin: true, requiresRole: 1 },
    },
    {
      path: '/office/setting',
      name: 'admin-setting',
      component: () => import('../views/admin/SettingView.vue'),
      meta: { admin: true, requiresAdmin: true, requiresRole: 1 },
    },
    {
      path: '/office/users',
      name: 'admin-users',
      component: () => import('../views/admin/AdminUsersView.vue'),
      meta: { admin: true, requiresAdmin: true, requiresRole: 2 },
    },
    {
      path: '/office/:pathMatch(.*)*',
      name: 'admin-not-found',
      component: () => import('../views/admin/NotFoundView.vue'),
      meta: { admin: true },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../views/NotFoundView.vue'),
    },
  ],
})

router.beforeEach((to) => {
  const { isLoggedIn, isAdmin, isSyx, isUserx, user } = useAuth()

  if (to.meta.requiresAdmin && !isAdmin.value) {
    return { name: 'admin-login', query: { redirect: to.fullPath } }
  }

  if (to.meta.requiresRole !== undefined && isSyx.value && user.value) {
    const userRole = user.value.role ?? 100
    if (userRole > (to.meta.requiresRole as number)) {
      return { name: 'admin-dashboard' }
    }
  }

  if (to.meta.adminGuest && isAdmin.value) {
    return { name: 'admin-dashboard' }
  }

  if (to.meta.requiresAuth && !isLoggedIn.value) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (to.meta.userType === 'userx' && !isUserx.value) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (to.meta.guest && isUserx.value) {
    return { name: 'home' }
  }
})

export default router
